import { useRef } from 'react';
import { Calendar, AlertCircle } from 'lucide-react';
import dayjs from 'dayjs';

import { InfoTip } from '@/components/ui/info-tip';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { formatDateWithFullMonth } from '../../../../util/formatting';
import type { FormErrors } from '../hooks/useAccessFormValidation';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SchedulingFormProps {
  firstAvailableDateStatus: string;
  firstAvailableDate: string | null;
  onStatusChange: (status: string) => void;
  onDateChange: (date: string | null) => void;
  isCartOrder: boolean;
  errors: FormErrors;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SchedulingForm = ({
  firstAvailableDateStatus,
  firstAvailableDate,
  onStatusChange,
  onDateChange,
  isCartOrder,
  errors
}: SchedulingFormProps) => {
  const dateInputRef = useRef<HTMLInputElement>(null);

  const today = dayjs();
  const minDate = today.format('YYYY-MM-DD');
  const maxDate = today.add(6, 'days').format('YYYY-MM-DD');

  const handleStatusChange = (value: string) => {
    onStatusChange(value);
    if (value === 'Yes') {
      onDateChange(null);
    }
  };

  const handleDateInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value?.length > 0) {
      onDateChange(formatDateWithFullMonth(value));
    } else {
      onDateChange(null);
    }
  };

  const isDisabled = firstAvailableDateStatus === 'Yes';
  const hasDateError = !!errors.firstAvailableDate;

  return (
    <div className="space-y-3">
      {/* Radio: Is property ready now? */}
      <div className="space-y-2">
        <Label className="text-base font-normal">
          Is the property ready now?
          <span className="text-destructive ml-0.5">*</span>
        </Label>
        <RadioGroup
          value={firstAvailableDateStatus}
          onValueChange={handleStatusChange}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Yes" id="first-avail-yes" />
            <Label
              htmlFor="first-avail-yes"
              className="text-base font-normal cursor-pointer"
            >
              Yes, head over ASAP
            </Label>
          </div>
          <div className="flex items-start space-x-2">
            <RadioGroupItem
              value="No"
              id="first-avail-no"
              className="mt-1"
            />
            <div className="flex items-start gap-1">
              <Label
                htmlFor="first-avail-no"
                className="text-base font-normal cursor-pointer"
              >
                No. Please schedule my appointment on or shortly after the
                date below.
              </Label>
              <InfoTip
                label="About scheduling on or after the selected date"
                side="bottom"
                triggerClassName="mt-1"
                contentClassName="max-w-[400px] p-2.5"
              >
                We will do our best to schedule your appointment on the
                selected date, but we cannot guarantee it.
              </InfoTip>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Date picker button + hidden native input */}
      <div className={isCartOrder ? 'w-full' : 'w-6/12'}>
        <button
          type="button"
          disabled={isDisabled}
          className={`flex h-[41px] w-full items-center justify-between rounded-md border px-3 py-2 text-sm ${
            isDisabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer'
          } ${
            hasDateError
              ? 'border-destructive'
              : 'border-input'
          } bg-background`}
          onClick={() => dateInputRef.current?.showPicker()}
          data-testid="order-date"
        >
          <span
            className={
              firstAvailableDate && firstAvailableDateStatus !== 'Yes'
                ? 'text-foreground'
                : 'text-muted-foreground'
            }
          >
            {firstAvailableDate && firstAvailableDateStatus !== 'Yes'
              ? firstAvailableDate
              : 'Choose date'}
          </span>
          <span className="flex items-center gap-2">
            {hasDateError && (
              <AlertCircle className="h-5 w-5 text-destructive" />
            )}
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </span>
        </button>

        <input
          ref={dateInputRef}
          type="date"
          className="sr-only"
          min={minDate}
          max={maxDate}
          onChange={handleDateInputChange}
          aria-label="firstAvailableDate"
        />

        {hasDateError && (
          <p className="text-sm text-destructive mt-1">
            {errors.firstAvailableDate}
          </p>
        )}
      </div>
    </div>
  );
};

export default SchedulingForm;
